import { getCookie } from './cookie';

export const MOOSEND_WEBSITE_ID = process.env.REACT_APP_MOOSEND_WEBSITE_ID;

const TRACKING_SCRIPT_URL = '//cdn.stat-track.com/statics/moosend-tracking.min.js';

export const isMoosendEnabled = () => {
  return process.env.REACT_APP_ENABLE_MOOSEND === 'true';
};

export const isMoosendTrackingEnabled = () => {
  return isMoosendEnabled() && !!MOOSEND_WEBSITE_ID && MOOSEND_WEBSITE_ID !== 'change-me';
};

const hasMarketingConsent = () => {
  const { isAddsEnabled } = getCookie() || {};
  return isMoosendTrackingEnabled() && isAddsEnabled;
};

const isMoosendConfigured = () => {
  return !!MOOSEND_WEBSITE_ID && MOOSEND_WEBSITE_ID !== 'change-me';
};

const safelyCallMootrack = (...args) => {
  try {
    if (!window.mootrack) return false;
    window.mootrack(...args);
    return true;
  } catch (e) {
    console.warn('Moosend tracking call failed', e);
    return false;
  }
};

export const loadMoosendTracking = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  if (!hasMarketingConsent() || !isMoosendConfigured()) return false;

  if (window.mootrack && window.__moosendTrackingLoaded) {
    safelyCallMootrack('init', MOOSEND_WEBSITE_ID);
    return true;
  }

  if (!window.mootrack) {
    window.MooTrackerObject = 'mootrack';
    window.mootrack = function() {
      window.mootrack.q
        ? window.mootrack.q.push(arguments)
        : (window.mootrack.q = [arguments]);
    };
  }

  if (!document.querySelector(`script[src*="${TRACKING_SCRIPT_URL.replace('//', '')}"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `${TRACKING_SCRIPT_URL}?ts=${~~(Date.now() / 3e5)}`;
    document.head.appendChild(script);
  }

  safelyCallMootrack('init', MOOSEND_WEBSITE_ID);
  window.__moosendTrackingLoaded = true;
  return true;
};

export const trackMoosendPageView = () => {
  if (!loadMoosendTracking()) return false;
  return safelyCallMootrack('trackPageView');
};

// Returns true only when tracking is active (marketing consent given) and the
// identify call was actually dispatched, so callers can tell consent-gated
// identification apart from a no-op.
export const identifyMoosendUser = userOrEmail => {
  if (!loadMoosendTracking() || !userOrEmail) return false;

  if (typeof userOrEmail === 'string') {
    return safelyCallMootrack('identify', userOrEmail);
  }

  const { attributes } = userOrEmail || {};
  const { email, profile } = attributes || {};
  const { firstName, lastName, displayName } = profile || {};
  const name = displayName || [firstName, lastName].filter(Boolean).join(' ');

  if (email && name) {
    return safelyCallMootrack('identify', email, name);
  } else if (email) {
    return safelyCallMootrack('identify', email);
  }

  return false;
};

const imageUrlFromItem = item => {
  return item.item_image || item.itemImage || item.image || item.imageUrl || '';
};

const urlFromItem = item => {
  return item.item_url || item.itemUrl || item.url || item.moosendItemUrl || window.location.href;
};

const moosendProductFromItem = item => {
  const price = Number(item.price || item.value || 0);
  const quantity = Number(item.quantity || 1);

  return {
    itemCode: item.item_id || item.id,
    itemName: item.item_name || item.name || item.title || item.item_id || item.id,
    itemImage: imageUrlFromItem(item),
    itemPrice: price,
    itemUrl: urlFromItem(item),
    itemQuantity: quantity,
    itemTotalPrice: Number(item.itemTotalPrice || price * quantity),
    itemCategory: item.item_category || item.category,
    itemManufacturer: item.item_brand || item.brand || item.manufacturer,
  };
};

export const trackMoosendEvent = (eventName, params = {}) => {
  if (!loadMoosendTracking()) return false;

  const items = Array.isArray(params.items) ? params.items : [];
  const product = items[0]
    ? moosendProductFromItem({
        ...items[0],
        itemImage: params.moosendItemImage,
        itemUrl: params.moosendItemUrl,
      })
    : null;

  if (eventName === 'view_item' && product) {
    return safelyCallMootrack('PAGE_VIEWED', [{ product }]);
  }

  if ((eventName === 'add_to_cart' || eventName === 'begin_checkout') && product) {
    return safelyCallMootrack(
      'trackAddToOrder',
      product.itemCode,
      product.itemPrice,
      product.itemUrl,
      product.itemQuantity,
      product.itemTotalPrice,
      product.itemName,
      product.itemImage,
      {
        itemCategory: product.itemCategory,
        itemManufacturer: product.itemManufacturer,
      }
    );
  }

  if (eventName === 'purchase' && items.length > 0) {
    return safelyCallMootrack(
      'trackOrderCompleted',
      items.map(item =>
        moosendProductFromItem({
          ...item,
          itemImage: item.itemImage || item.item_image || params.moosendItemImage,
          itemUrl: item.itemUrl || item.item_url || params.moosendItemUrl,
        })
      )
    );
  }

  if (eventName === 'add_to_wishlist') {
    return safelyCallMootrack('add_to_wishlist', {
      product_id:
        params.product_id ||
        params.item_id ||
        params.id ||
        items[0]?.item_id ||
        items[0]?.id,
    });
  }

  if (eventName === 'signup') {
    return safelyCallMootrack('signup');
  }

  return false;
};
